import type { ImsRole } from './ims-roles';
import { seatSeedDeltasById } from './seat-profiles';
import type { LayerKey } from './layer-preferences';

/**
 * Role → seat-profile boot scaffold (#458, role-view council §7 step 4 —
 * docs/role-view-council-2026-06.md).
 *
 * On FIRST RUN (an operator with no stored layer-preference rows yet) the COP can
 * come up already configured for their IMS seat by seeding the matching
 * seat-profile's layer deltas. This map is the seam: it pairs each EXISTING IMS
 * role with the seed whose default-on layer set fits that seat.
 *
 * DELIBERATELY PARTIAL — only the operational roles that have a doctrinal seat
 * seed are mapped:
 *   - situation-unit → seed:situation-unit
 *   - logistics      → seed:logistics
 *   - liaison        → seed:liaison
 * `admin` (the AUTH-off demo identity), `eoc-director`, and `viewer` are
 * intentionally UNMAPPED → they boot to the locked DEFAULT_VIEW_LAYERS unchanged,
 * byte-identical to today (the lock is untouched).
 *
 * SCAFFOLD, NOT LIVE TODAY: there is NO 'municipal'/'peoc' IMS role (ims-roles.ts),
 * and AUTH_ENFORCED ships OFF (one demo operator, role 'admin', unmapped) — so this
 * map seeds nothing observable under AUTH-off. It maps the roles that exist so the
 * mechanism is in place; the Municipal/PEOC auto-boot is a NAMED follow-on gated on
 * the RBAC role expansion + AUTH-enforcement. The persisted "pick Municipal once →
 * it sticks" boot is carried by ordinary layer-preference persistence, not this map.
 *
 * NOT an auth model — role scopes the DEFAULT layer set, never the capability
 * (council §5.1). The seed is applied as DELTAS over the locked default
 * (applyProfileLayerDeltas), so it can never fork or fossilize DEFAULT_VIEW_LAYERS.
 */
export const ROLE_TO_SEAT_SEED: Partial<Record<ImsRole, string>> = {
  'situation-unit': 'seed:situation-unit',
  logistics: 'seed:logistics',
  liaison: 'seed:liaison',
};

/**
 * The layer deltas to seed on first run for a role, or null when the role is
 * unmapped (→ the caller applies an empty delta set = DEFAULT_VIEW_LAYERS). Pure:
 * resolves the role's seed id to a fresh copy of that seed's deltas.
 */
export function seatSeedDeltasForRole(role: ImsRole): Partial<Record<LayerKey, boolean>> | null {
  const seedId = ROLE_TO_SEAT_SEED[role];
  return seedId ? seatSeedDeltasById(seedId) : null;
}
